import { inject, Injectable, OnDestroy } from '@angular/core';
import { RestClientFactory } from '../../market/rest-client-factory';
import { WebsocketClientFactory, WebsocketClientWrapper } from '../../market/websocket-client-factory';
import { Order } from '../../model/trade';
import { TradeHistoryService } from '../history/trade-history.service';
import { debounceTime, finalize, map, merge, Observable, of, shareReplay, Subject, Subscription, switchMap, tap } from 'rxjs';
import { OrderHandler } from '../realtime/order.handler';
import { OrderHelper } from '../order.helper';
import { TradeKeyWithPermissions } from '../../model/common';

@Injectable({
  providedIn: 'root'
})
export class OrdersFlowService implements OnDestroy {

  public readonly rcf = inject(RestClientFactory)
  public readonly wcf = inject(WebsocketClientFactory)
  public readonly tradeHistory = inject(TradeHistoryService)
  public readonly orderHandler = inject(OrderHandler)
  public readonly orderHelper = inject(OrderHelper)

  private flows = new Map<number, OrdersFlow>()

  getFlow(key: TradeKeyWithPermissions) {
    let flow = this.flows.get(key.id)
    if (!flow) {
      flow = new OrdersFlow(key, this)
      this.flows.set(key.id, flow)
    }
    return flow
  }

  ngOnDestroy(): void {
    this.flows.forEach(flow => flow.destroy())
  }

}


class OrdersFlow {

  private orders: Order[] = []
  private restart$ = new Subject<void>()
  private restartTrigger = () => this.restart$.next()

  public readonly orders$: Observable<Order[]>
  private sb: Subscription

  constructor(public readonly key: TradeKeyWithPermissions, private service: OrdersFlowService) {
    if (key.isDemo || !key.canRead) {
      // Для демо/без прав на чтение возвращаем пустой поток без запросов к бирже.
      this.orders$ = of([]).pipe(
        shareReplay(1)
      )
    }
    else {
      const rest = this.service.rcf.createForKey(key)
      const ws = this.service.wcf.createForKey(key)

      this.bind(ws)

      this.orders$ = merge(
        // Первый запрос ордеров
        this.service.tradeHistory.requestActiveOrders(rest).pipe(
          tap(orders => this.orders = orders),
        ),
        // Последующие запросы ордеров при переподключении
        this.restart$.pipe(
          debounceTime(1000),
          // После реконнекта источник истины — REST-слепок.
          switchMap(() => this.service.tradeHistory.requestActiveOrders(rest)),
          tap(orders => this.orders = orders),
        ),
        // Обработка событий вебсокета
        this.service.orderHandler.listen(ws).pipe(
          map(order => {
            this.processOrder(order)
            return this.orders
          }),
        )
      ).pipe(
        finalize(() => this.unbind(ws)),
        // shareReplay нужен, чтобы несколько подписчиков получали единый кеш
        // и не создавали повторные запросы к бирже.
        shareReplay(1)
      )
    }

    // Подписка нужна, чтобы запустить поток и держать ws-обработчики активными.
    this.sb = this.orders$.subscribe()
  }

  destroy() {
    this.restart$.complete()
    this.sb.unsubscribe()
  }

  private bind(ws: WebsocketClientWrapper) {
    // TODO привести события на вебсокетах к единому виду
    if (ws.isBinance) {
      ws.binance.on('reconnected', this.restartTrigger)
    }
    if (ws.isBybit) {
      ws.bybit.on('reconnected', this.restartTrigger)
    }
  }

  private unbind(ws: WebsocketClientWrapper) {
    // TODO привести события на вебсокетах к единому виду
    if (ws.isBinance) {
      ws.binance.off('reconnected', this.restartTrigger)
    }
    if (ws.isBybit) {
      ws.bybit.off('reconnected', this.restartTrigger)
    }
  }

  /**
   * Обрабатывает новый ордер, прилетевший по ws
   */
  private processOrder(newOrder: Order) {
    const isActive = this.service.orderHelper.isActiveStatusOrder(newOrder)

    if (isActive) {
      const existsOrder = this.findOrder(newOrder)
      if (existsOrder) {
        // обновляем существующий ордер
        existsOrder.updateTimestamp = newOrder.updateTimestamp
        existsOrder.status = newOrder.status
        existsOrder.price = newOrder.price
        existsOrder.quantity = newOrder.quantity
        existsOrder.remainingQuantity = newOrder.remainingQuantity
        existsOrder.remainingVolume = newOrder.remainingVolume
      } else {
        // добавляем новый ордер
        this.orders.push(newOrder)
      }
    }
    else {
      // удаляем закрытый ордер
      const i = this.findOrderIndex(newOrder)
      if (i >= 0) {
        this.orders.splice(i, 1)
      }
    }
  }

  /**
   * Находит ордер в массиве
   */
  private findOrder(searchOrder: Order) {
    return this.orders.find(o => o.market == searchOrder.market && o.board == searchOrder.board && o.symbol == searchOrder.symbol && o.orderId == searchOrder.orderId)
  }

  /**
   * Находит ордер в массиве
   */
  private findOrderIndex(searchOrder: Order) {
    return this.orders.findIndex(o => o.market == searchOrder.market && o.board == searchOrder.board && o.symbol == searchOrder.symbol && o.orderId == searchOrder.orderId)
  }

}
